import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { LockIcon, PauseIcon, PlayIcon } from 'lucide-react'
import Spinner from '@tandem/ui/Spinner'
import Table, { SortHeader, TableFooter, TableRegion } from '@tandem/ui/Table'
import formatDate from '@tandem/ui/util/formatDate'
import $type from '@tandem/ui/type.css'
import $layout from '@tandem/ui/layout.css'
import $form from '@tandem/ui/form.css'
import $anim from '@tandem/ui/anim.css'
import { useTRPC } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import { plural } from '@/layer/Thread'
import $ from './-Sync.css'

export const Route = createFileRoute('/sync/$schema')({ component: SyncPage })

let volume = (rows: string | number, bytes: number): number => Number(rows) * bytes
let fmtBytes = (v: number): string =>
  v >= 1e9 ? `${(v / 1e9).toFixed(1)} ГБ`
    : v >= 1e6 ? `${(v / 1e6).toFixed(0)} МБ`
      : v >= 1e3 ? `${(v / 1e3).toFixed(0)} КБ`
        : `${v} Б`
let fmtNum = (v: string | number | null): string => v === null ? '—' : Number(v).toLocaleString('ru')
let fmt = (d: Date | string | null | undefined): string => d ? formatDate(d) : '—'
let busy = (status?: string): boolean => status === 'pending' || status === 'running'
let locked = (r: { error: string | null }): boolean => /HTTP 401\b/.test(r.error ?? '')
let LOCKED_HINT = 'Нет доступа: у пользователя OData в 1С нет прав на этот объект, поэтому он не синхронизируется. Выдайте права в 1С: объём появится после ночного сканирования.'

function SyncPage() {
  let { company } = useAuth()
  let { schema } = Route.useParams()
  let trpc = useTRPC()
  let qc = useQueryClient()
  let [sort, setSort] = useState<'entity' | 'size'>('size')
  let [picked, setPicked] = useState<Set<string>>(new Set())
  let [since, setSince] = useState('')
  let isAdmin = company?.role === 'admin'

  // Воркер в другом контейнере — прогресс тянем поллингом.
  let { data: status } = useQuery({
    ...trpc.sync.status.queryOptions({ schema }),
    refetchInterval: q => busy(q.state.data?.scan?.status) || q.state.data?.sync?.enabled ? 2000 : 10000,
  })
  let scan = status?.scan
  let sync = status?.sync
  let { data: rows = [], isFetching, isSuccess: loaded } = useQuery({
    ...trpc.sync.inventory.queryOptions({ schema }),
    refetchInterval: busy(scan?.status) || sync?.current ? 5000 : false,
  })

  let invalidate = () => {
    qc.invalidateQueries({ queryKey: trpc.sync.status.queryKey({ schema }) })
    qc.invalidateQueries({ queryKey: trpc.sync.inventory.queryKey({ schema }) })
  }
  let startScan = useMutation(trpc.sync.scan.mutationOptions({ onSuccess: invalidate }))
  let startSync = useMutation(trpc.sync.sync.mutationOptions({ onSuccess: invalidate }))
  let pause = useMutation(trpc.sync.pause.mutationOptions({ onSuccess: invalidate }))
  let resume = useMutation(trpc.sync.resume.mutationOptions({ onSuccess: invalidate }))
  let autoAdd = useMutation(trpc.sync.autoAdd.mutationOptions({ onSuccess: invalidate }))

  // Таблицу надо перечитать в момент завершения скана и каждой сущности —
  // иначе после долгого сканирования экран пуст, а последняя сущность цикла
  // показывает устаревшее состояние.
  let changeKey = `${scan?.id}:${scan?.status}:${sync?.position}:${sync?.current}`
  let seen = useRef(changeKey)
  useEffect(() => {
    if (changeKey === seen.current) return
    seen.current = changeKey
    qc.invalidateQueries({ queryKey: trpc.sync.inventory.queryKey({ schema }) })
  }, [changeKey, qc, trpc])

  // Поле показывает дату снимка, чекбоксы — настроенный состав, пока человек
  // их не поменял.
  let primed = useRef(false)
  useEffect(() => {
    if (primed.current || !rows.length || !status) return
    primed.current = true
    setSince(rows[0].since ? new Date(rows[0].since).toISOString().slice(0, 10) : '')
    setPicked(new Set(status.sync?.entities ?? []))
  }, [rows, status])

  let sorted = useMemo(() => {
    let list = [...rows].sort(sort === 'entity'
      ? (a, b) => a.entity.localeCompare(b.entity, 'ru')
      : (a, b) => volume(b.rows, b.bytes_per_row) - volume(a.rows, a.bytes_per_row))
    return list.sort((a, b) => Number(locked(b)) - Number(locked(a)))
  }, [rows, sort])
  let open = sorted.filter(r => !locked(r))

  // Дата снимка — источник истины для переноса; поле сверху может от неё уйти,
  // и тогда показанная оценка больше не описывает то, что поедет.
  let snapshotSince = rows[0]?.since ? new Date(rows[0].since) : null
  let staleDate = sorted.length > 0 && since !== (snapshotSince ? snapshotSince.toISOString().slice(0, 10) : '')

  let total = sorted.reduce((s, r) => s + volume(r.rows, r.bytes_per_row), 0)
  // Табличная часть едет вместе со своим документом.
  let coveredBy = (list: Iterable<string>, entity: string) => [...list].some(p => entity === p || entity.startsWith(`${p}_`))
  let parentOf = (entity: string) => sorted.find(r => entity.startsWith(`${r.entity}_`))?.entity
  let covered = sorted.filter(r => coveredBy(picked, r.entity))
  let pickedTotal = covered.reduce((s, r) => s + volume(r.rows, r.bytes_per_row), 0)
  let allPicked = open.length > 0 && covered.length === open.length
  // Сверка сравнима, только когда снимок описывает то же окно, что и синхронизация.
  let checked = !!sync?.entities.length && rows.some(r => r.local !== null) && (sync.since ? new Date(sync.since).getTime() : null) === (snapshotSince?.getTime() ?? null)
  let mismatch = (r: typeof rows[number]) => checked && !r.error && !r.estimated && coveredBy(sync!.entities, r.entity) && Number(r.local ?? 0) !== Number(r.rows)
  let problems = open.filter(r => r.error || mismatch(r)).length
  let toggle = (entity: string) => setPicked(prev => {
    let next = new Set(prev)
    let target = parentOf(entity) ?? entity
    if (coveredBy(prev, entity)) [entity, target].forEach(e => next.delete(e))
    else next.add(target)
    return next
  })

  // Старт применяет состав и дату оценки, Стоп ставит на паузу; пока идёт перенос, состав не меняется.
  let running = !!sync?.enabled && !!sync.entities.length
  let configured = new Set(sync?.entities ?? [])
  let changed = !configured.size || picked.size !== configured.size || [...picked].some(e => !configured.has(e))
    || (sync?.since ? new Date(sync.since).getTime() : null) !== (snapshotSince?.getTime() ?? null)
  let start = () => changed ? startSync.mutate({ schema, entities: [...picked] }) : resume.mutate({ schema })

  // Оценка идёт сама: при смене даты и ночью; кнопка для неё не нужна.
  let estimated = useRef<string | null>(null)
  useEffect(() => {
    if (!isAdmin || !loaded || !status || running || busy(scan?.status) || !(staleDate || !rows.length) || estimated.current === since) return
    let t = setTimeout(() => { estimated.current = since; startScan.mutate({ schema, since: since || null }) }, 800)
    return () => clearTimeout(t)
  }, [since, staleDate, running, scan?.status, rows.length, loaded, !!status])

  let syncedRows = new Map(rows.map(r => [r.entity, Number(r.synced_rows ?? 0)]))
  let phase = sync?.cycles ? 'Дельта' : 'Первичная выгрузка'
  let syncStatus = !sync?.entities.length ? 'Синхронизация не настроена.'
    : sync.current ? (
      <>
        <Spinner />
        {!sync.cycles && <progress class={$.bar} value={sync.position} max={sync.total} />}
        {phase}: {sync.position + 1} из {sync.total} · {sync.current}
        {(syncedRows.get(sync.current) ?? 0) > 0 && ` · ${fmtNum(syncedRows.get(sync.current)!)} строк`}
      </>
    )
    : !sync.enabled ? `На паузе${sync.cycles ? `, данные на ${fmt(sync.cycle_finished_at)}` : sync.position ? `: ${sync.position} из ${sync.total}` : ''}.`
    : sync.position > 0 || !sync.next_at ? <><Spinner /> {phase}: {sync.position ? `${sync.position} из ${sync.total}` : 'готовится'}</>
    : `Данные на ${fmt(sync.cycle_finished_at)}, следующая дельта ${fmt(sync.next_at)}.`

  return (
    <div class={$.page}>
      <div class={clsx($layout.container, $layout.center, $.controls)}>
        <h3>Синхронизация 1С</h3>

        <div class={clsx($form.row, $.controlRow)}>
          <label class={$.date}>
            Данные с
            <input type='date' value={since} onChange={e => setSince(e.currentTarget.value)} disabled={!isAdmin || running} />
          </label>
          {running
            ? <button class='secondary' disabled={!isAdmin} onClick={() => pause.mutate({ schema })}><PauseIcon size={14} /> Стоп</button>
            : <button disabled={!isAdmin || !picked.size || staleDate || busy(scan?.status)} onClick={start}><PlayIcon size={14} /> Старт</button>}
          {!!sync?.entities.length && (
            <label class={$.date} title='Ночная сверка дописывает в синхронизацию всё, что появилось в 1С'>
              <input type='checkbox' checked={sync.auto_add} disabled={!isAdmin} onChange={e => autoAdd.mutate({ schema, enabled: e.currentTarget.checked })} />
              Добавлять новые сущности
            </label>
          )}
        </div>

        <p class={clsx($type.small, $type.muted, $.hint)}>
          {busy(scan?.status) ? (
            <>
              <Spinner />
              {scan!.progress.total
                ? `Сканирование: ${scan!.progress.done ?? 0} из ${scan!.progress.total} · ${scan!.progress.current}`
                : 'Сканирование: читает метаданные'}
            </>
          ) : scan?.status === 'failed' ? <span class={$type.error}>Ошибка сканирования: {scan.error}</span>
            : sorted.length ? (
              <span>
                Оценка {snapshotSince ? `с ${snapshotSince.toLocaleDateString('ru')}` : 'за всё время'}:
                {' '}сущностей {sorted.length}, около {fmtBytes(total)}.
                {open.length < sorted.length && <span title={LOCKED_HINT}> Нет доступа к {plural(sorted.length - open.length, 'сущности', 'сущностям', 'сущностям')}.</span>}
                {checked && <> Сверено с 1С {fmt(rows[0].measured_at)}: {problems ? <span class={$type.error}>расхождений {problems}</span> : 'расхождений нет'}.</>}
                {running && isAdmin && ' Чтобы поменять дату или состав, остановите синхронизацию.'}
              </span>
            ) : 'Сканирование считает строки и объём каждой сущности с указанной даты, ничего не выгружая. Дата отсекает документы и регистры, справочники переносятся целиком.'}
          {!isAdmin && ' Запуск доступен администратору.'}
        </p>
      </div>

      <TableRegion>
        {sorted.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th class={$.pick}>
                  <input
                    type='checkbox'
                    checked={allPicked}
                    disabled={running}
                    onChange={() => setPicked(allPicked ? new Set() : new Set(open.map(r => r.entity)))}
                  />
                </th>
                <SortHeader dir={sort === 'entity' ? 'asc' : null} onSort={() => setSort('entity')}>Сущность</SortHeader>
                <SortHeader class={$.num} dir={sort === 'size' ? 'desc' : null} onSort={() => setSort('size')}
                  title='Точное число строк × средний размер строки по выборке; у табличных частей — доля от родителя'>Объём ≈</SortHeader>
                <th class={$.num}>Строк</th>
                <th title='Строк записано в последнем цикле; в дельте 0 — изменений не было'>Изменено за цикл</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.entity} class={clsx(r.entity === sync?.current && $anim.pulse, locked(r) && $type.muted)} title={locked(r) ? LOCKED_HINT : undefined}>
                  <td class={$.pick}>
                    <input
                      type='checkbox'
                      checked={coveredBy(picked, r.entity)}
                      disabled={locked(r) || running}
                      onChange={() => toggle(r.entity)}
                      title={!locked(r) && parentOf(r.entity) ? 'Табличная часть — выбирается вместе с документом' : undefined}
                    />
                  </td>
                  <td>{locked(r) && <LockIcon size={14} class={$.lock} />}{r.entity}</td>
                  <td class={$.num} title={r.estimated ? 'Оценка по доле от родительского документа' : undefined}>
                    {r.estimated && <span class={$type.muted}>≈ </span>}
                    {fmtBytes(volume(r.rows, r.bytes_per_row))}
                  </td>
                  <td class={clsx($.num, !locked(r) && (r.error || mismatch(r)) ? $type.error : $type.muted)} title={locked(r) ? undefined : r.error ?? undefined}>
                    {locked(r) ? 'нет доступа' : r.error ? 'не посчитано' : fmtNum(r.rows)}
                    {mismatch(r) && ` · у нас ${fmtNum(r.local ?? 0)}`}
                  </td>
                  <td class={r.sync_error ? $type.error : $type.muted} title={r.sync_error ?? undefined}>
                    {Number(r.synced_rows) ? fmtNum(r.synced_rows) : '—'}
                    {r.watermark && ` · до ${new Date(r.watermark).toLocaleDateString('ru')}`}
                    {r.sync_error && ' · ошибка'}
                  </td>
                </tr>
              ))}
            </tbody>
            <TableFooter colSpan={5} status={syncStatus} count={<>Выбрано: {covered.length} из {open.length}{covered.length > 0 && ` · около ${fmtBytes(pickedTotal)}`}</>} />
          </Table>
        ) : (
          <div class={$.emptyState}>
            {isFetching || busy(scan?.status) ? <Spinner /> : <strong>Данных пока нет — запустите сканирование.</strong>}
          </div>
        )}
      </TableRegion>
    </div>
  )
}
